import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { StorageType, StoreEvidenceRequest } from '../../../types/custody-actions'
import { usePerformCustodyAction } from '../../../hooks/use-custody-actions'
import { EvidenceItemSelector } from './EvidenceItemSelector'

interface StoreEvidenceFormProps {
  caseId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface StoreFormState {
  selectedIds: string[]
  actionDate: string
  actionTime: string
  storageLocation: string
  storageType: StorageType
  notes: string
}

const DATE_SLICE_END = 10
const TIME_SLICE_END = 5

function buildInitialState(): StoreFormState {
  const now = new Date()
  return {
    selectedIds: [],
    actionDate: now.toISOString().slice(0, DATE_SLICE_END),
    actionTime: now.toTimeString().slice(0, TIME_SLICE_END),
    storageLocation: '',
    storageType: 'physical',
    notes: '',
  }
}

function buildRequest(state: StoreFormState): StoreEvidenceRequest {
  const req: StoreEvidenceRequest = {
    evidenceNumberIds: state.selectedIds,
    actionDate: state.actionDate,
    actionTime: state.actionTime,
    storageLocation: state.storageLocation,
    storageType: state.storageType,
  }
  if (state.notes.length > 0) req.notes = state.notes
  return req
}

type Updater = <K extends keyof StoreFormState>(key: K, value: StoreFormState[K]) => void

interface StoreSelectorSectionProps {
  caseId: string
  state: StoreFormState
  update: Updater
}

function StoreSelectorSection({
  caseId,
  state,
  update,
}: StoreSelectorSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <EvidenceItemSelector
        caseId={caseId}
        selectedIds={state.selectedIds}
        onChange={(ids) => {
          update('selectedIds', ids)
        }}
      />
      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="store-date" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionDate')}
          </label>
          <input
            id="store-date"
            type="date"
            value={state.actionDate}
            onChange={(ev) => {
              update('actionDate', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="store-time" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionTime')}
          </label>
          <input
            id="store-time"
            type="time"
            value={state.actionTime}
            onChange={(ev) => {
              update('actionTime', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
    </>
  )
}

interface StoreFormFieldsProps {
  caseId: string
  state: StoreFormState
  update: Updater
  error: string | null
}

function StoreFormFields({
  caseId,
  state,
  update,
  error,
}: StoreFormFieldsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="space-y-4 overflow-y-auto p-6">
      <StoreSelectorSection caseId={caseId} state={state} update={update} />
      <div>
        <label htmlFor="store-location" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.storageLocation')}
        </label>
        <input
          id="store-location"
          type="text"
          value={state.storageLocation}
          onChange={(ev) => {
            update('storageLocation', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <p className="mb-1 text-xs text-[var(--text-muted)]">{t('custodyActions.storageType')}</p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="store-type"
              value="physical"
              checked={state.storageType === 'physical'}
              onChange={() => {
                update('storageType', 'physical')
              }}
            />
            {t('custodyActions.physical')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="store-type"
              value="digital"
              checked={state.storageType === 'digital'}
              onChange={() => {
                update('storageType', 'digital')
              }}
            />
            {t('custodyActions.digital')}
          </label>
        </div>
      </div>
      <div>
        <label htmlFor="store-notes" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.notes')}
        </label>
        <textarea
          id="store-notes"
          value={state.notes}
          onChange={(ev) => {
            update('notes', ev.target.value)
          }}
          rows={2}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  )
}

export function StoreEvidenceForm({
  caseId,
  open,
  onClose,
  onSuccess,
}: StoreEvidenceFormProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const [state, setState] = useState<StoreFormState>(buildInitialState)
  const { performing, error, performStore, clearError } = usePerformCustodyAction()

  const update = useCallback(<K extends keyof StoreFormState>(key: K, value: StoreFormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleSubmit = useCallback(async () => {
    const result = await performStore(caseId, buildRequest(state))
    if (result !== null) onSuccess()
  }, [state, caseId, performStore, onSuccess])

  const handleClose = useCallback(() => {
    clearError()
    onClose()
  }, [clearError, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
            {t('custodyActions.store')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <StoreFormFields caseId={caseId} state={state} update={update} error={error} />
        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded border border-[var(--border)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
          >
            {t('custodyActions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={performing || state.selectedIds.length === 0}
            className="rounded bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-white uppercase hover:opacity-90 disabled:opacity-50"
          >
            {t('custodyActions.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
